import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { useRegimeEdge, RegimeAlignmentDot } from "@/components/RegimeEdge";
import { ChainSourceBadge, ChainSourceBanner } from "@/components/ChainSource";
import { RegimeSojournBar } from "@/components/RegimeSojournBar";
import { computeTradeSignals, signalColor, freshnessLabel, qualityTint } from "@/lib/tradeSignals";
import { verdictDisplay, NO_VISIBILITY_COLOR, noVisibilityNote } from "@/lib/displayLabels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, Search, ArrowRight,
  TrendingUp, AlertTriangle, Layers, Gauge, Clock, Info, ClipboardList, Zap,
} from "lucide-react";
import { PmccView } from "./PmccView";

const MONO = { fontFamily: "JetBrains Mono, monospace" };

const REGIME_COLORS = {
  "Quiet Range": "#3B82F6",
  "Bullish Trend": "#10B981",
  "Bearish Trend": "#EF4444",
  Volatile: "#F59E0B",
};

const CLASS_STYLES = {
  "High Model Alignment": { color: "#3B82F6", border: "#3B82F6" },
  "Moderate Alignment": { color: "var(--hoip-text-secondary)", border: "var(--hoip-text-tertiary)" },
  "Mixed Signal": { color: "#F59E0B", border: "#F59E0B" },
  "Regime Mismatch": { color: "#EF4444", border: "#EF4444" },
};

const REGIME_OPTIONS = ["all", "Bullish Trend", "Quiet Range", "Volatile", "Bearish Trend"];
const CLASS_OPTIONS = ["all", "High Model Alignment", "Moderate Alignment", "Mixed Signal", "Regime Mismatch"];
// Display-only relabel (compliance): backend classification enums unchanged.
const CLASS_DISPLAY = { "Mixed Signal": "Mixed Indicators" };
const classDisplay = (c) => CLASS_DISPLAY[c] || c;

function fmt(v, d = 2) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toFixed(d);
}

function money(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`;
}

// ── Campaign sizing ────────────────────────────────────────────
// Quality guards so the auto-basket never dumps 1000+ contracts of a thin,
// far-OTM, weak-fundamental name to "fill" the target.
const ROR_FLOOR = 0.5;          // %: premium must yield ≥0.5% on risk/collateral
const PREM_FLOOR = 15;          // $/contract: ignore sub-$15 premiums
const PER_NAME_CAP_PCT = 0.20;  // no single name funds >20% of the target

// Portfolio-healthy risk budgets as a fraction of NLV. BPS uses total *defined
// risk*; CSP/CC use total *collateral / shares value* (the capital at risk).
const RISK_MODES = {
  conservative: { label: "Conservative", bps: 0.12, csp: 0.25, perName: 0.03, sector: 0.15 },
  moderate: { label: "Moderate", bps: 0.18, csp: 0.35, perName: 0.05, sector: 0.20 },
  aggressive: { label: "Aggressive", bps: 0.25, csp: 0.50, perName: 0.08, sector: 0.30 },
};

// Short-leg philosophy labels — keep in sync with the strike-mode toggle.
const STRIKE_MODE_LABELS = { delta: "Δ Regime-Scaled", key_levels: "Key Levels", pnt: "P(NT)" };

// P2 — regime-aware minimum premium-yield targets (mirrors backend
// services/strike_engine.REGIME_PREMIUM_TARGET). Yields below the regime target
// surface a warning; the Yield % cell colours against the same per-regime cutoff.
const REGIME_YIELD_TARGET = {
  "Quiet Range": 0.3,
  "Bullish Trend": 0.4,
  "Bearish Trend": 0.5,
  Volatile: 0.6,
};
const DEFAULT_YIELD_TARGET = 0.5;
function regimeYieldTarget(regime) {
  return REGIME_YIELD_TARGET[regime] ?? DEFAULT_YIELD_TARGET;
}

// Classify an earnings date relative to a row's selected expiry.
// → null | { kind:'spans'|'proximity', date, confirmed, daysAfter?, avgMove }
function classifyEarnings(e, expiry) {
  if (!e || !e.date || !expiry) return null;
  const ed = new Date(`${e.date}T00:00:00`);
  const ex = new Date(`${String(expiry).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(ed.getTime()) || Number.isNaN(ex.getTime())) return null;
  const diff = Math.round((ed - ex) / 86400000); // + = earnings AFTER expiry
  const base = { date: e.date, confirmed: e.confirmed !== false, avgMove: e.avg_move_pct };
  if (diff <= 0) return { ...base, kind: "spans" };
  if (diff <= 5) return { ...base, kind: "proximity", daysAfter: diff };
  return null;
}

function buildCampaign(cands, target, opts) {
  const { maxPerName, nlv = 0, totalPct = 1, perNamePct = 1, sectorPct = 1 } = opts || {};
  const hasTarget = Number(target) > 0;
  const totalBudget = nlv > 0 ? nlv * totalPct : Infinity;
  const perNameBudget = nlv > 0 ? nlv * perNamePct : Infinity;
  const sectorBudget = nlv > 0 ? nlv * sectorPct : Infinity;
  const ranked = cands
    .filter(r => (r._prem ?? 0) >= PREM_FLOOR && (r._ror ?? 0) >= ROR_FLOOR && !r.low_quality && !r.suppressed && !r._price_estimated)
    .sort((a, b) => (b.composite_score ?? 0) - (a.composite_score ?? 0));
  const perPremCap = hasTarget ? target * PER_NAME_CAP_PCT : Infinity;
  let remaining = hasTarget ? target : Infinity;
  let riskUsed = 0;
  const sectorUsed = {};
  let riskConstrained = false;
  const out = [];
  for (const r of ranked) {
    if (remaining <= 0) break;
    const prem = r._prem;
    const risk = r._risk || 0;
    if (risk <= 0 || prem <= 0) continue;
    const sector = r.sector || "Unknown";
    // ── premium / hard-count ceiling (income goal + per-name diversification) ──
    const hardCt = r._max_ct != null ? Math.min(maxPerName, r._max_ct) : maxPerName;
    const needed = hasTarget ? Math.ceil(remaining / prem) : hardCt;
    const byPremCap = hasTarget ? Math.max(1, Math.floor(perPremCap / prem)) : hardCt;
    const premDesired = Math.min(needed, byPremCap, hardCt);
    // ── crash-control risk caps (consume long-wing max_loss_per_spread via _risk) ──
    const byPerNameRisk = Math.floor(perNameBudget / risk);
    const byTotalRisk = Math.floor((totalBudget - riskUsed) / risk);
    const bySectorRisk = Math.floor((sectorBudget - (sectorUsed[sector] || 0)) / risk);
    const contracts = Math.max(0, Math.min(premDesired, byPerNameRisk, byTotalRisk, bySectorRisk));
    if (contracts < 1) {
      if (premDesired >= 1) riskConstrained = true; // wanted some, risk budget blocked it
      continue;
    }
    // ── which constraint set the final count? (priority: per-name → sector → NLV → goal) ──
    const newSectorRisk = (sectorUsed[sector] || 0) + contracts * risk;
    const secUsedPct = nlv > 0 ? Math.round((newSectorRisk / nlv) * 100) : 0;
    const secCapPct = Math.round(sectorPct * 100);
    const binders = [
      { v: byPerNameRisk, label: "per-name cap" },
      { v: bySectorRisk, label: `sector cap (${sector} ${secUsedPct}%/${secCapPct}%)` },
      { v: byTotalRisk, label: "NLV cap" },
      { v: premDesired, label: hasTarget ? "income target" : "max/name" },
    ];
    const binder = binders.find(b => b.v === contracts) || binders[0];
    if (contracts < premDesired) riskConstrained = true; // risk caps trimmed below premium target
    out.push({ ...r, _contracts: contracts, _premCollected: contracts * prem, _capRisk: contracts * risk, _binding: binder.label });
    if (hasTarget) remaining -= contracts * prem;
    riskUsed += contracts * risk;
    sectorUsed[sector] = newSectorRisk;
  }
  if (hasTarget && remaining > 0 && nlv > 0) riskConstrained = true; // target unmet within risk budget
  return { basket: out, riskUsed, totalBudget: nlv > 0 ? totalBudget : null, riskConstrained };
}

function summarize(list, target) {
  const premium = list.reduce((s, r) => s + (r._premCollected ?? 0), 0);
  const risk = list.reduce((s, r) => s + (r._capRisk ?? 0), 0);
  const contracts = list.reduce((s, r) => s + (r._contracts ?? 0), 0);
  const blendedWin = contracts ? list.reduce((s, r) => s + (r.bt_win_rate || 0) * (r._contracts ?? 0), 0) / contracts : 0;
  return {
    names: list.length, contracts, premium, risk, blendedWin,
    premRiskPct: risk > 0 ? (premium / risk) * 100 : 0,
    pct: target > 0 ? Math.min(100, (premium / target) * 100) : 0,
  };
}

function timeAgo(iso) {
  if (!iso) return "never";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const Pill = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} type="button"
    className="px-3 py-1.5 text-xs rounded-md border transition-colors whitespace-nowrap"
    style={{
      borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
      background: active ? "var(--hoip-text-primary)" : "transparent",
      color: active ? "#0A0A0A" : "var(--hoip-text-secondary)",
      fontWeight: active ? 600 : 400,
    }}>
    {children}
  </button>
);

function KpiCard({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid}
      className="flex flex-col justify-between rounded-md border p-4 min-h-[88px]"
      style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--hoip-text-secondary)" }}>{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent || "var(--hoip-text-tertiary)" }} />}
      </div>
      <div className="mt-2">
        <div className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif", color: accent || "var(--hoip-text-primary)" }}>{value}</div>
        {sub && <div className="text-[10px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>{sub}</div>}
      </div>
    </div>
  );
}

function RegimeCell({ regime, posterior }) {
  const c = REGIME_COLORS[regime] || "var(--hoip-text-tertiary)";
  return (
    <div className="flex items-center gap-2">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c }} />
      <div className="flex flex-col leading-tight">
        <span className="text-xs" style={{ color: "var(--hoip-text-primary)" }}>{regime}</span>
        <span className="text-[10px]" style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>{fmt(posterior, 0)}% confidence</span>
      </div>
    </div>
  );
}

function CspStrikes({ row }) {
  const k = row.income_strike;
  if (k == null) return <span style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>—</span>;
  return <span style={{ ...MONO, color: "var(--hoip-text-primary)" }}>${fmt(k, 0)}</span>;
}

function BpsStrikes({ row }) {
  if (!row.bps_short) return <span style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>—</span>;
  return (
    <div className="flex flex-col items-end leading-tight" style={MONO} data-testid={`bps-strikes-${row.ticker}`}>
      <div className="flex items-center gap-1">
        <span className="font-medium px-1 rounded-sm" style={{ color: "#10B981", background: "rgba(16,185,129,0.12)" }}>${fmt(row.bps_short, 0)}</span>
        <span style={{ color: "var(--hoip-text-tertiary)" }}>/</span>
        <span style={{ color: "var(--hoip-text-secondary)" }}>${fmt(row.bps_long, 0)}</span>
      </div>
      <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}
        title={row.bps_long_method ? `Long wing: ${row.bps_long_method}` : undefined}>
        width ${fmt(row.bps_width, 0)}{row.bps_long_method ? " · wing: shared" : ""}
      </span>
      {row.bps_long_warning && (
        <span className="text-[9px]" style={{ color: "#F59E0B" }} data-testid={`bps-long-warning-${row.ticker}`} title={row.bps_long_warning}>
          ⚠ {row.bps_long_warning}
        </span>
      )}
    </div>
  );
}

function CcStrikes({ row }) {
  if (!row.cc_strike) return <span style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>—</span>;
  const basis = (row.avg_cost && row.avg_cost > 0) ? row.avg_cost : null;
  return (
    <div className="flex flex-col items-end leading-tight" style={MONO}>
      <span className="font-medium px-1 rounded-sm" style={{ color: "#3B82F6", background: "rgba(59,130,246,0.12)" }}>${fmt(row.cc_strike, row.cc_strike < 30 ? 1 : 0)} call</span>
      {basis != null && <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>cost ${fmt(basis, basis < 30 ? 2 : 0)}</span>}
    </div>
  );
}

function ClassBadge({ cls }) {
  const s = CLASS_STYLES[cls] || CLASS_STYLES["Moderate Alignment"];
  return (
    <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm border whitespace-nowrap"
      style={{ color: s.color, borderColor: s.border, background: "transparent" }}>
      {classDisplay(cls)}
    </span>
  );
}

// ── Trade-readiness instrument panel (3 sequential gauges per row) ──
function MiniGauge({ value, width = 44 }) {
  const c = signalColor(value);
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <span className="inline-flex items-center gap-1">
      <span className="rounded-sm overflow-hidden" style={{ width, height: 5, background: "var(--hoip-border)" }}>
        <span style={{ display: "block", height: "100%", width: `${pct}%`, background: c, transition: "width 0.3s ease" }} />
      </span>
      <span className="text-[9px] font-mono-data" style={{ color: c, minWidth: 18 }}>{value == null ? "—" : value}</span>
    </span>
  );
}

function TradeReadinessCell({ r, strategy }) {
  const s = r._sig || computeTradeSignals(r, strategy);
  const fr = freshnessLabel(s.phi);
  const gapColor = signalColor(s.strikeAlignment);
  const isCC = strategy === "cc";
  const conflicts = isCC ? r.cc_conflicts : r.conflicts;
  return (
    <div className="flex flex-col gap-1 min-w-[190px]" data-testid={`readiness-cell-${r.ticker}`}>
      {/* ① Regime Health */}
      <div className="flex items-center gap-1.5" data-testid={`readiness-regime-${r.ticker}`}>
        <span className="text-[8px] w-3 text-center rounded-sm" style={{ color: "var(--hoip-text-tertiary)" }}>1</span>
        <span className="text-[9px] w-10 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>Regime</span>
        <MiniGauge value={s.regimeHealth} />
        {fr && <span className="text-[8px] font-mono-data px-1 rounded-sm" style={{ color: fr.color, background: `${fr.color}1a` }}
          title={`Regime freshness φ=${s.phi} — ${fr.label}`}>φ{s.phi} {fr.label}</span>}
      </div>
      {/* ② Strike Alignment — most prominent actionable element (delta gap) */}
      <div className="flex items-center gap-1.5 rounded-sm px-1 py-0.5" data-testid={`readiness-strike-${r.ticker}`}
        style={{ background: s.deltaDir && s.deltaDir !== "aligned" ? "rgba(245,158,11,0.06)" : "transparent" }}>
        <span className="text-[8px] w-3 text-center rounded-sm" style={{ color: "var(--hoip-text-tertiary)" }}>2</span>
        <span className="text-[9px] w-10 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>Strike</span>
        {s.deltaCurrent != null ? (
          <span className="text-[10px] font-mono-data font-semibold flex items-center gap-1" style={{ color: gapColor }}
            data-testid={`delta-gap-${r.ticker}`}
            title={s.deltaAction ? `Current Δ${s.deltaCurrent.toFixed(2)} vs regime-optimal Δ${s.deltaTarget.toFixed(2)} — ${s.deltaAction}` : undefined}>
            Δ{s.deltaCurrent.toFixed(2)}
            <ArrowRight className="w-2.5 h-2.5" />
            Δ{s.deltaTarget.toFixed(2)}
            {s.deltaDir && s.deltaDir !== "aligned" && (
              <span className="text-[8px] uppercase tracking-wide px-1 rounded-sm" style={{ color: gapColor, background: `${gapColor}1a` }}>{s.deltaDir}</span>
            )}
          </span>
        ) : <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>n/a</span>}
      </div>
      {/* ③ Sizing Confidence */}
      <div className="flex items-center gap-1.5" data-testid={`readiness-sizing-${r.ticker}`}>
        <span className="text-[8px] w-3 text-center rounded-sm" style={{ color: "var(--hoip-text-tertiary)" }}>3</span>
        <span className="text-[9px] w-10 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>Sizing</span>
        <MiniGauge value={s.sizingConfidence} />
        {s.recommendedSizePct != null && (
          <span className="text-[8px] font-mono-data" style={{ color: signalColor(s.sizingConfidence) }}
            title="Readiness-based size estimate (% of normal)">{s.recommendedSizePct}% size</span>
        )}
      </div>
      {/* Secondary context — demoted classification + conflicts */}
      <div className="flex items-center gap-1 flex-wrap mt-0.5">
        <ClassBadge cls={isCC ? r.cc_classification : r.classification} />
        {r.beyond_visibility && (() => {
          const ev = isCC ? r.cc_execution_verdict : r.execution_verdict;
          const pnt = strategy === "bps" ? (r.bps_pnt ?? r.income_pnt) : isCC ? r.cc_pnt : r.income_pnt;
          return (
            <>
              <span className="text-[8px] px-1 py-0.5 rounded-sm font-mono-data" data-testid={`no-visibility-${r.ticker}`}
                title={noVisibilityNote(ev?.dte ?? r.dte, ev?.sojourn_p75, pnt)}
                style={{ color: NO_VISIBILITY_COLOR, background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.30)" }}>
                No visibility at {Math.round(ev?.dte ?? r.dte)}d · P(NT) {pnt != null ? `${pnt}%` : "—"}
              </span>
              <PntCreditSparkline ladder={r.pnt_credit_ladder} />
            </>
          );
        })()}
        {conflicts?.length > 0 && (
          <span className="flex items-center gap-0.5 text-[8px]" style={{ color: "#F59E0B" }} title={conflicts.join(" · ")}>
            <Info className="w-2.5 h-2.5" /> {conflicts[0]}
          </span>
        )}
      </div>
    </div>
  );
}

function SortHeader({ label, field, sortField, sortDir, onSort, align = "right", title }) {
  const active = sortField === field;
  const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className={`px-3 py-2 text-${align} font-medium select-none cursor-pointer`} onClick={() => onSort(field)}
      title={title} data-testid={`sort-${field}`}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {label}
        <Icon className="w-3 h-3" style={{ opacity: active ? 1 : 0.4 }} />
      </span>
    </th>
  );
}

// ── Earnings awareness: lazy batch fetch (24h server cache) ────
function useScreenerEarnings(tickers) {
  const [earn, setEarn] = useState({});
  const key = (tickers || []).slice(0, 80).join(",");
  useEffect(() => {
    if (!key) return;
    let alive = true;
    api.get("/screener/earnings", { params: { tickers: key } })
      .then(({ data }) => { if (alive) setEarn(prev => ({ ...prev, ...(data?.results || {}) })); })
      .catch(() => {});
    return () => { alive = false; };
  }, [key]);
  return earn;
}

// Amber/yellow earnings chip shown below the Model-Alignment badge.
function EarningsChip({ cls, ticker }) {
  if (!cls) return null;
  const tilde = cls.confirmed ? "" : "~";
  const d = shortDate(cls.date);
  const moveLine = cls.avgMove ? ` Historical avg move: ±${cls.avgMove}%` : "";
  const spans = cls.kind === "spans";
  const tip = spans
    ? `Earnings on ${cls.date} fall before expiry. The model's regime and P(NT) assumptions do not account for earnings volatility.${moveLine}`
    : `Earnings on ${cls.date} fall ${cls.daysAfter}d after expiry. The model's regime and P(NT) assumptions do not account for earnings volatility.${moveLine}`;
  const color = "#F59E0B";
  return (
    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm whitespace-nowrap"
      data-testid={`earnings-chip-${ticker}`} title={tip}
      style={{ color, background: spans ? "rgba(245,158,11,0.14)" : "rgba(245,158,11,0.07)", border: `1px solid ${color}${spans ? "55" : "33"}` }}>
      ⚡ EPS {tilde}{d}{spans ? " before exp" : ` +${cls.daysAfter}d post`}
    </span>
  );
}

// IV-Rank badge (data already on the evidence row as r.iv_rank).
function IvrBadge({ ivr, ticker, method }) {
  if (ivr === null || ivr === undefined || Number.isNaN(Number(ivr))) return null;
  const v = Math.round(Number(ivr));
  const isTrue = method === "true_ivr";
  const color = v >= 50 ? "#10B981" : v < 25 ? "#F59E0B" : "var(--hoip-text-tertiary)";
  const tip = isTrue
    ? `True 52-week IV Rank ${v}: today's ATM IV positioned within this ticker's stored 52-week implied-vol range (60+ daily samples).`
    : `~IV Rank ${v} — IV vs 52-wk realized-vol band (proxy — differs from broker-style 52-week IV Rank, which requires historical implied-vol data we don't currently store).`;
  return (
    <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded-sm whitespace-nowrap"
      data-testid={`ivr-badge-${ticker}`} title={tip}
      style={{ color, background: `${color === "var(--hoip-text-tertiary)" ? "transparent" : color}14`, border: `1px solid ${color === "var(--hoip-text-tertiary)" ? "var(--hoip-border)" : `${color}33`}`, ...MONO }}>
      {isTrue ? "" : "~"}IVR {v}
    </span>
  );
}

// C3 — per-row data-quality chip. Iter 106 iconography cleanup: brand-name
// badges (YF / Tradier "T") no longer render next to the ticker symbol —
// provider provenance lives in the cluster-level ChainSourceBanner above the
// table. Only the functional "degraded" warning remains here.
function ProviderChip({ source, degraded }) {
  if (degraded) {
    return (
      <span data-testid="provider-degraded" title="Broker data unavailable for this row — figures may be incomplete."
        className="text-[8px] px-1 rounded-sm font-bold" style={{ color: "#EF4444", background: "rgba(239,68,68,0.12)" }}>
        degraded
      </span>
    );
  }
  return null;
}

// Per-row price staleness cue (Feature 3) computed from the shared evidence age.
function staleInfo(evidenceAsOf) {
  if (!evidenceAsOf) return null;
  const hrs = (Date.now() - new Date(evidenceAsOf).getTime()) / 3600000;
  if (Number.isNaN(hrs)) return null;
  const label = timeAgo(evidenceAsOf);
  if (hrs > 4) return { label, color: "#EF4444", warn: true,
    tip: "Evidence is stale. Click Refresh Evidence to update prices and recalculate P(NT), yield, and credit." };
  if (hrs > 2) return { label, color: "#F59E0B", warn: true, tip: null };
  return { label, color: "var(--hoip-text-tertiary)", warn: false, tip: null };
}

// P1 — Single-ticker re-screen button for Stale/Degraded rows. Calls the
// existing /screener/refresh-tickers endpoint so the user can revive one row
// without waiting for the 30-min full rebuild. On success, patches the row
// directly in local state.
function RescreenButton({ ticker, horizon, strikeMode, onPatched }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(
        `/screener/refresh-tickers?tickers=${ticker}&horizon=${horizon || "d35"}&strike_mode=${strikeMode || "delta"}`
      );
      const row = (res?.rows || []).find(r => r.ticker === ticker);
      if (row) onPatched?.(row);
    } catch { /* swallow — row stays stale, retry is idempotent */ }
    finally { setBusy(false); }
  };
  return (
    <button data-testid={`rescreen-${ticker}`} onClick={handle} disabled={busy}
      title="Re-screen this ticker without rebuilding the full universe"
      className="inline-flex items-center gap-0.5 text-[9px] px-1 rounded-sm font-semibold ml-1"
      style={{ color: busy ? "var(--hoip-text-tertiary)" : "#3B82F6",
               background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.30)" }}>
      <RefreshCw className={`w-2.5 h-2.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "…" : "Re-screen"}
    </button>
  );
}

// P2 — Tiny P(NT)-vs-credit sparkline for "no visibility" rows. Plots the
// alt-strike ladder the backend computed (5–7 puts around the short-leg) so
// users can rank trades by their P(NT) ↔ credit tradeoff when the regime model
// has run out of forward visibility. Pure SVG, no chart library.
function PntCreditSparkline({ ladder }) {
  if (!Array.isArray(ladder) || ladder.length < 2) return null;
  const W = 72, H = 18, PAD = 1.5;
  const credits = ladder.map(d => d.credit);
  const pnts = ladder.map(d => d.p_nt);
  const cMin = Math.min(...credits), cMax = Math.max(...credits);
  const pMin = Math.min(...pnts), pMax = Math.max(...pnts);
  const cSpan = (cMax - cMin) || 1;
  const pSpan = (pMax - pMin) || 1;
  const points = ladder.map(d => {
    const x = PAD + ((d.credit - cMin) / cSpan) * (W - 2 * PAD);
    const y = H - PAD - ((d.p_nt - pMin) / pSpan) * (H - 2 * PAD);
    return [x, y];
  });
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const tip = `P(NT)-vs-credit (no-visibility horizon)\n${ladder.map(d => `· $${d.strike} → $${d.credit} credit · ${d.p_nt}% P(NT)`).join("\n")}\nUp & right = safer + richer (sweet spot).`;
  return (
    <svg data-testid="pnt-credit-sparkline" width={W} height={H} className="ml-1" title={tip}
      role="img" aria-label="P(NT) versus credit ladder">
      <title>{tip}</title>
      <path d={path} fill="none" stroke="#3B82F6" strokeWidth="1.2" strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="1.4" fill="#3B82F6" opacity={i === Math.floor(points.length / 2) ? 1 : 0.55} />
      ))}
    </svg>
  );
}

export default function ScreenerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isProOrAdmin = user?.role === "admin" || ["pro", "legacy_pro"].includes(user?.tier);
  const [data, setData] = useState({ opportunity: [], conflict: [], portfolio: [], kpis: {}, evidence_as_of: null, status: "loading" });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("opportunity");
  const [horizon, setHorizon] = useState(null);
  const [horizons, setHorizons] = useState([]);
  const [strategy, setStrategy] = useState("csp"); // csp | bps | cc
  const [strikeMode, setStrikeMode] = useState("delta"); // delta | key_levels | pnt (short-leg philosophy)
  const [search, setSearch] = useState("");
  const [regimeFilter, setRegimeFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [verdictFilter, setVerdictFilter] = useState("all");
  const [minRegimeHealth, setMinRegimeHealth] = useState(0);
  const [minStrikeAlign, setMinStrikeAlign] = useState(0);
  const [minSizing, setMinSizing] = useState(0);
  const [minPnt, setMinPnt] = useState("");
  const [maxRisk, setMaxRisk] = useState("");
  const [minYield, setMinYield] = useState("");
  const [minRor, setMinRor] = useState("");
  const [maxRor, setMaxRor] = useState("");
  const [excludeNegExp, setExcludeNegExp] = useState(false);
  const [excludeEarnings, setExcludeEarnings] = useState(false);
  const [maxDD, setMaxDD] = useState("");
  const [targetIncome, setTargetIncome] = useState("");
  const [basketMode, setBasketMode] = useState("auto"); // auto | manual
  const [maxPerName, setMaxPerName] = useState(20);
  const [nlv, setNlv] = useState(120000);
  const [riskMode, setRiskMode] = useState("moderate"); // conservative | moderate | aggressive
  const [hideOpenCalls, setHideOpenCalls] = useState(true);
  const [hideWeak, setHideWeak] = useState(false);
  // Iter 106 — sector shard + hide-unaligned (readiness) toggle
  const [sectorShard, setSectorShard] = useState("All");
  const [sectorOptions, setSectorOptions] = useState([{ name: "All", count: 0 }]);
  const [hideUnaligned, setHideUnaligned] = useState(false);
  // Iter 107 — surface paste-poisoned broker credentials (Cyrillic look-alikes
  // etc.) so the user can fix them instead of silently falling back to yfinance
  // for every fetch. Read once from /broker/active-source on mount.
  const [credErrors, setCredErrors] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [manualCts, setManualCts] = useState({});
  const [sortField, setSortField] = useState("composite_score");
  const [sortDir, setSortDir] = useState("desc");
  const pollRef = useRef(null);
  const prefsLoaded = useRef(false);
  const saveTimer = useRef(null);

  const load = useCallback(async (h, sm) => {
    try {
      const { data: d } = await api.get("/screener/model", { params: { ...(h ? { horizon: h } : {}), strike_mode: sm || "delta" } });
      setData(d);
      return d;
    } catch (e) { console.error("screener load", e); return null; }
    finally { setLoading(false); }
  }, []);

  const pollStatus = useCallback((h, sm) => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data: s } = await api.get("/screener/status", { params: { ...(h ? { horizon: h } : {}), strike_mode: sm || "delta" } });
        // ── Phase 1: progressive hydration ──
        // Each poll tick during a 'running' build also re-reads the model so
        // newly-completed 15-ticker bursts render as soon as they hit cache,
        // instead of the table waiting for all ~300 names to resolve.
        if (s.run?.status === "running") {
          await load(h, sm);
        } else if (s.run?.status === "done" || s.run?.status === "error") {
          clearInterval(pollRef.current); pollRef.current = null;
          setRefreshing(false);
          await load(h, sm);
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, [load]);

  useEffect(() => {
    api.get("/screener/horizons").then(({ data }) => {
      setHorizons(data.horizons || []);
      setHorizon((prev) => prev || data.default);
    }).catch(() => {});
    // Iter 106 — sector shard options for the dropdown
    api.get("/screener/sectors").then(({ data }) => {
      if (Array.isArray(data?.sectors)) setSectorOptions(data.sectors);
    }).catch(() => {});
    // Iter 107 — broker credential health (paste-poisoned chars get flagged)
    api.get("/broker/active-source").then(({ data }) => {
      if (Array.isArray(data?.cred_errors)) setCredErrors(data.cred_errors);
    }).catch(() => {});
  }, []);

  // P1 — Patch a single row across all buckets after a /refresh-tickers call.
  // Lets the Re-screen button revive one stale/degraded row without touching
  // the rest of the ~290 rows.
  const patchRow = useCallback((row) => {
    if (!row?.ticker) return;
    setData(prev => {
      const swap = (arr) => Array.isArray(arr) ? arr.map(r => r.ticker === row.ticker ? row : r) : arr;
      const dropFrom = (arr) => Array.isArray(arr) ? arr.filter(r => r.ticker !== row.ticker) : arr;
      // The rebuilt row may have escaped Stale/Degraded — drop it from those
      // buckets and let it land back in Opportunity/Conflict on next load.
      return {
        ...prev,
        opportunity: swap(prev.opportunity),
        conflict: swap(prev.conflict),
        portfolio: swap(prev.portfolio),
        degraded: row.degraded ? swap(prev.degraded) : dropFrom(prev.degraded),
        stale: row.stale ? swap(prev.stale) : dropFrom(prev.stale),
      };
    });
  }, []);


  // ── Sticky filters: hydrate persisted state once on mount ──
  useEffect(() => {
    api.get("/screener/prefs").then(({ data }) => {
      const p = data?.prefs || {};
      if (p && Object.keys(p).length) {
        if (p.strategy) setStrategy(p.strategy);
        if (p.strikeMode) setStrikeMode(p.strikeMode);
        if (p.savedHorizon) setHorizon(p.savedHorizon);
        if (p.regimeFilter) setRegimeFilter(p.regimeFilter);
        if (p.classFilter) setClassFilter(p.classFilter);
        if (p.verdictFilter) setVerdictFilter(p.verdictFilter);
        if (p.minRegimeHealth !== undefined) setMinRegimeHealth(Number(p.minRegimeHealth) || 0);
        if (p.minStrikeAlign !== undefined) setMinStrikeAlign(Number(p.minStrikeAlign) || 0);
        if (p.minSizing !== undefined) setMinSizing(Number(p.minSizing) || 0);
        if (p.minPnt !== undefined) setMinPnt(p.minPnt);
        if (p.maxRisk !== undefined) setMaxRisk(p.maxRisk);
        if (p.hideWeak !== undefined) setHideWeak(!!p.hideWeak);
        if (p.minYield !== undefined) setMinYield(p.minYield);
        if (p.minRor !== undefined) setMinRor(p.minRor);
        if (p.maxRor !== undefined) setMaxRor(p.maxRor);
        if (p.maxDD !== undefined) setMaxDD(p.maxDD);
        if (p.excludeNegExp !== undefined) setExcludeNegExp(!!p.excludeNegExp);
        if (p.excludeEarnings !== undefined) setExcludeEarnings(!!p.excludeEarnings);
        if (p.targetIncome !== undefined) setTargetIncome(p.targetIncome);
        if (p.basketMode) setBasketMode(p.basketMode);
        if (p.maxPerName !== undefined) setMaxPerName(p.maxPerName);
        if (p.nlv !== undefined) setNlv(p.nlv);
        if (p.riskMode) setRiskMode(p.riskMode);
      }
      prefsLoaded.current = true;
    }).catch(() => { prefsLoaded.current = true; });
  }, []);

  // ── Sticky filters: debounced persist on any control change ──
  useEffect(() => {
    if (!prefsLoaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/screener/prefs", { prefs: {
        strategy, strikeMode, savedHorizon: horizon,
        regimeFilter, classFilter, verdictFilter, minPnt, maxRisk, hideWeak,
        minRegimeHealth, minStrikeAlign, minSizing,
        minYield, minRor, maxRor, maxDD, excludeNegExp, excludeEarnings,
        targetIncome, basketMode, maxPerName, nlv, riskMode,
      } }).catch(() => {});
    }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [strategy, strikeMode, horizon, regimeFilter, classFilter, verdictFilter, minPnt, maxRisk,
      hideWeak, minRegimeHealth, minStrikeAlign, minSizing, minYield, minRor, maxRor, maxDD, excludeNegExp, excludeEarnings,
      targetIncome, basketMode, maxPerName, nlv, riskMode]);

  const resetFilters = () => {
    setRegimeFilter("all"); setClassFilter("all"); setVerdictFilter("all");
    setMinRegimeHealth(0); setMinStrikeAlign(0); setMinSizing(0);
    setMinPnt(""); setMaxRisk(""); setHideWeak(false);
    setMinYield(""); setMinRor(""); setMaxRor(""); setMaxDD("");
    setExcludeNegExp(false); setExcludeEarnings(false);
  };

  useEffect(() => {
    if (!horizon) return;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setLoading(true);
    (async () => {
      const d = await load(horizon, strikeMode);
      if (d?.status === "building" || d?.status === "running") { setRefreshing(true); pollStatus(horizon, strikeMode); }
    })();
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [horizon, strikeMode, load, pollStatus]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/screener/refresh", null, {
        params: {
          horizon, strike_mode: strikeMode,
          ...(sectorShard && sectorShard !== "All" ? { sector: sectorShard } : {}),
        },
      });
      pollStatus(horizon, strikeMode);
    } catch (e) { console.error("refresh", e); setRefreshing(false); }
  };

  const onSort = (field) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  // Earnings universe = current tab's tickers (independent of row filtering so
  // the "Exclude earnings-spanning" filter can act on a complete map).
  const baseTickers = useMemo(() => (data[tab] || []).slice(0, 80).map(r => r.ticker), [data, tab]);
  const earnings = useScreenerEarnings(baseTickers);

  const rows = useMemo(() => {
    // STRICT PARTITION (trust contract): each tab renders ONLY its own bucket.
    // The backend guarantees opportunity / conflict / degraded / stale are
    // disjoint — a Regime-Mismatch or NO-GO name can never pose as an
    // opportunity, and chain-less rows live in the Degraded tab.
    const isDataTab = tab === "degraded" || tab === "stale";
    const srcList = data[tab] || [];
    let list = srcList.map(r => {
      let econ;
      if (strategy === "bps") {
        // Δ / P(NT) belong to the DISPLAYED short leg (bps_delta/bps_pnt);
        // income-strike metrics are only a legacy fallback for old cache rows.
        econ = { _prem: r.bps_credit_dollars, _ror: r.bps_ror, _risk: r.bps_risk, _pnt: r.bps_pnt ?? r.income_pnt, _delta: r.bps_delta ?? r.income_delta, _yield: r.income_yield_pct };
      } else if (strategy === "cc") {
        // Covered call (held-only). Static yield = premium ÷ cost basis;
        // ROR column = if-called total return = (premium + (strike − basis)) ÷ basis.
        // Risk $ = share market value at risk (spot × 100). Contracts capped by held shares.
        const basis = (r.avg_cost && r.avg_cost > 0) ? r.avg_cost : r.price;
        const prem = r.cc_premium;
        const strike = r.cc_strike;
        econ = {
          _prem: r.cc_premium_dollars ?? (prem != null ? Math.round(prem * 100) : null),
          _ror: (prem != null && strike && basis) ? +(((prem + (strike - basis)) / basis) * 100).toFixed(2) : null,
          _yield: (prem != null && basis) ? +((prem / basis) * 100).toFixed(2) : null,
          _risk: r.price ? r.price * 100 : null,
          _pnt: r.cc_p_not_called,
          _delta: r.cc_delta,
          _max_ct: Math.floor((r.held_shares || 0) / 100) || null,
        };
      } else {
        // CSP economics must reflect the *same* strike that's displayed:
        //   - when the regime-safe strike diverges (sits below income) it is
        //     the shown/recommended strike → use its premium, P(NT), Δ.
        //   - otherwise the income strike is shown → use income metrics.
        // This keeps premium/yield/ROR/P(NT)/Δ matching the option chain for
        // the exact strike in the row.
        const usesSafe = r.strikes_diverge && r.safe_premium != null && r.safe_strike;
        const prem = usesSafe ? r.safe_premium : r.income_premium;
        const strike = usesSafe ? r.safe_strike : r.income_strike;
        econ = {
          _prem: prem != null ? Math.round(prem * 100) : null,
          _ror: (prem != null && strike) ? +(prem / strike * 100).toFixed(2) : null,
          _risk: strike ? strike * 100 : null,
          _pnt: usesSafe ? r.p_no_touch : r.income_pnt,
          _delta: usesSafe ? r.delta : r.income_delta,
          _yield: (prem != null && r.price) ? +(prem / r.price * 100).toFixed(2) : null,
        };
      }
      const priceEstimated = strategy === "bps" ? !!r.bps_price_estimated
        : strategy === "csp" ? !!r.csp_price_estimated : false;
      const merged = { ...r, ...econ, _price_estimated: priceEstimated, suppressed: strategy === "cc" && !!r.open_short_call };
      merged._sig = computeTradeSignals(merged, strategy);
      return merged;
    });
    if (search) list = list.filter(r => r.ticker?.toLowerCase().includes(search.toLowerCase()));
    // Iter 106 — sector shard (display filter; rows already carry r.sector
    // from the backend SECTOR_MAP / sector_watchlists).
    if (sectorShard && sectorShard !== "All") {
      list = list.filter(r => (r.sector || "").toLowerCase() === sectorShard.toLowerCase());
    }
    // Iter 106 — hide-unaligned (Readiness gate): drops rows whose verdict is
    // "Parameters Outside Standard Band" (NO-GO) OR whose DTE runs past the
    // regime's P75 sojourn (beyond_visibility). Suppresses on data-issue tabs.
    if (hideUnaligned && tab !== "degraded" && tab !== "stale") {
      list = list.filter(r => {
        const ev = strategy === "cc" ? r.cc_execution_verdict : r.execution_verdict;
        const v = (ev?.verdict || "").toUpperCase();
        const unaligned = v === "NO-GO" || v === "NO_GO" || !!r.beyond_visibility;
        return !unaligned;
      });
    }
    // Data-issue tabs (Degraded / Stale) show their full bucket — strategy and
    // quality filters would hide exactly the rows the user came to inspect.
    if (isDataTab) {
      const ddir = sortDir === "asc" ? 1 : -1;
      return [...list].sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        if (typeof av === "string") return (av || "").localeCompare(bv || "") * ddir;
        return ((av ?? -Infinity) - (bv ?? -Infinity)) * ddir;
      });
    }
    if (regimeFilter !== "all") list = list.filter(r => r.regime === regimeFilter);
    if (classFilter !== "all") list = list.filter(r => r.classification === classFilter);
    if (verdictFilter !== "all") list = list.filter(r => {
      const v = (strategy === "cc" ? r.cc_execution_verdict : r.execution_verdict)?.verdict;
      return v === verdictFilter;
    });
    // Independent per-dimension risk-factor sliders (Principle 4).
    if (minRegimeHealth > 0) list = list.filter(r => (r._sig?.regimeHealth ?? 0) >= minRegimeHealth);
    if (minStrikeAlign > 0) list = list.filter(r => (r._sig?.strikeAlignment ?? 0) >= minStrikeAlign);
    if (minSizing > 0) list = list.filter(r => (r._sig?.sizingConfidence ?? 0) >= minSizing);
    if (minPnt) { const m = Number(minPnt); list = list.filter(r => (r._pnt ?? -1) >= m); }
    if (maxRisk) { const m = Number(maxRisk); list = list.filter(r => r._risk != null && r._risk <= m); }
    if (minYield) { const m = Number(minYield); list = list.filter(r => (r._yield ?? -1) >= m); }
    if (minRor) { const m = Number(minRor); list = list.filter(r => (r._ror ?? -1) >= m); }
    if (maxRor) { const m = Number(maxRor); list = list.filter(r => (r._ror ?? Infinity) <= m); }
    if (excludeNegExp) list = list.filter(r => (r.bt_expectancy ?? 0) >= 0);
    if (excludeEarnings) list = list.filter(r => classifyEarnings(earnings[r.ticker], r.expiry)?.kind !== "spans");
    if (maxDD) { const m = Number(maxDD); list = list.filter(r => (r.bt_drawdown ?? Infinity) <= m); }
    if (hideWeak) list = list.filter(r => !r.low_quality);
    if (strategy === "bps") list = list.filter(r => r.bps_short != null);
    if (strategy === "cc") list = list.filter(r => r.held && r.cc_strike != null);
    if (strategy === "cc" && hideOpenCalls) list = list.filter(r => !r.open_short_call);
    const dir = sortDir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (typeof av === "string") return (av || "").localeCompare(bv || "") * dir;
      return ((av ?? -Infinity) - (bv ?? -Infinity)) * dir;
    });
    return list;
  }, [data, tab, strategy, search, regimeFilter, classFilter, verdictFilter, minRegimeHealth, minStrikeAlign, minSizing, minPnt, maxRisk, minYield, minRor, maxRor, excludeNegExp, excludeEarnings, earnings, maxDD, hideWeak, hideOpenCalls, sortField, sortDir]);

  // ── Auto campaign: quality-gated, diversified, contract- and risk-capped ──
  const riskCfg = useMemo(() => {
    const m = RISK_MODES[riskMode] || RISK_MODES.moderate;
    const totalPct = strategy === "bps" ? m.bps : m.csp; // cc uses collateral-style cap
    return { mode: m, totalPct, perNamePct: m.perName, sectorPct: m.sector };
  }, [riskMode, strategy]);

  const buildOpts = useMemo(() => ({
    maxPerName: Number(maxPerName) || 20,
    nlv: Number(nlv) || 0,
    totalPct: riskCfg.totalPct, perNamePct: riskCfg.perNamePct, sectorPct: riskCfg.sectorPct,
  }), [maxPerName, nlv, riskCfg]);

  // Per-name crash-control budget ($) = NLV × per-name % for the selected mode.
  // floor(perNameBudget / max_loss_per_spread) = max contracts a single name can take.
  const perNameBudgetUI = useMemo(() => {
    const n = Number(nlv) || 0;
    return n > 0 ? n * riskCfg.perNamePct : 0;
  }, [nlv, riskCfg]);

  const campaign = useMemo(() => {
    const t = Number(targetIncome);
    if (!t || t <= 0) return { basket: [], riskUsed: 0, totalBudget: null, riskConstrained: false };
    return buildCampaign(rows, t, buildOpts);
  }, [rows, targetIncome, buildOpts]);
  const autoBasket = campaign.basket;

  // Earnings-spanning names inside the auto-campaign basket (warn, don't remove).
  const earningsSpanning = useMemo(
    () => autoBasket.filter(r => classifyEarnings(earnings[r.ticker], r.expiry)?.kind === "spans").map(r => r.ticker),
    [autoBasket, earnings]
  );

  // BS-estimated rows excluded from the campaign (real chain unavailable).
  const bsExcludedCount = useMemo(() => rows.filter(r => r._price_estimated).length, [rows]);

  const basketSummary = useMemo(() => {
    if (basketMode !== "auto" || autoBasket.length === 0) return null;
    return summarize(autoBasket, Number(targetIncome));
  }, [autoBasket, basketMode, targetIncome]);

  // ── Manual à-la-carte selection (checkbox + editable contracts) ──
  const manualList = useMemo(() => rows
    .filter(r => selected.has(r.ticker))
    .map(r => {
      const c = Math.max(1, manualCts[r.ticker] || 1);
      return { ...r, _contracts: c, _premCollected: c * (r._prem || 0), _capRisk: c * (r._risk || 0) };
    }), [rows, selected, manualCts]);
  const manualSummary = useMemo(() => summarize(manualList, Number(targetIncome) || 0), [manualList, targetIncome]);

  const toggleSelect = (ticker) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(ticker)) next.delete(ticker);
    else { next.add(ticker); setManualCts(m => (m[ticker] ? m : { ...m, [ticker]: 1 })); }
    return next;
  });
  const setCts = (ticker, v, max) => {
    let n = Math.max(1, Math.floor(Number(v) || 1));
    if (max != null && max > 0) n = Math.min(n, max);
    setManualCts(m => ({ ...m, [ticker]: n }));
  };
  const selectToTarget = () => {
    const t = Number(targetIncome) || 0;
    if (t <= 0) return;
    const { basket } = buildCampaign(rows, t, buildOpts);
    setSelected(new Set(basket.map(r => r.ticker)));
    setManualCts(Object.fromEntries(basket.map(r => [r.ticker, r._contracts])));
  };

  // Covered call is held-only → pin to the Portfolio tab when selected.
  useEffect(() => {
    if (strategy === "cc") setTab("portfolio");
  }, [strategy]);

  const isManual = basketMode === "manual";
  const targeting = !isManual && Number(targetIncome) > 0 && autoBasket.length > 0;
  const displayRows = targeting ? autoBasket : rows;
  // Lazy per-regime edge for the visible rows (additive Model-Alignment dot).
  const regimeEdges = useRegimeEdge(displayRows.slice(0, 60).map(r => r.ticker));

  // ── Log basket → Trade Intents (planned trades) ──
  const [logState, setLogState] = useState(null); // null | "saving" | "done" | "error"
  const activeBasket = isManual ? manualList : autoBasket;
  const legsFromBasket = (list) => {
    const stratMap = { csp: "cash_secured_put", bps: "bull_put_spread", cc: "covered_call" };
    return list.map(r => {
      let short_strike = null, long_strike = null;
      if (strategy === "bps") { short_strike = r.bps_short ?? null; long_strike = r.bps_long ?? null; }
      else if (strategy === "cc") { short_strike = r.cc_strike ?? null; }
      else { short_strike = (r.strikes_diverge && r.safe_strike) ? r.safe_strike : (r.income_strike ?? null); }
      return {
        ticker: r.ticker,
        strategy: stratMap[strategy] || strategy,
        short_strike, long_strike,
        expiry: r.expiry || null,
        qty: r._contracts || 1,
        est_premium_per_contract: r._prem ?? null,
        est_total_premium: r._premCollected ?? null,
        est_capital_risk: r._capRisk ?? null,
        regime: r.regime || null,
        classification: r.classification || null,
      };
    });
  };
  const logBasket = async () => {
    if (!activeBasket.length || logState === "saving") return;
    setLogState("saving");
    try {
      await api.post("/screener/log-basket", { horizon, legs: legsFromBasket(activeBasket) });
      setLogState("done");
      setTimeout(() => setLogState(null), 4000);
    } catch (e) {
      console.error("log basket", e);
      setLogState("error");
      setTimeout(() => setLogState(null), 4000);
    }
  };
  const logBtnLabel = logState === "saving" ? "Logging…" : logState === "done" ? "Logged ✓" : logState === "error" ? "Failed — retry" : "Log basket to Trade Log";

  const k = data.kpis || {};
  const hiddenByFilters = Math.max(0, ((data[tab] || []).length) - rows.length);
  const building = data.status === "building" || data.status === "running" || refreshing;
  const isBps = strategy === "bps";
  const isCC = strategy === "cc";
  const isPmcc = strategy === "pmcc";

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-5" data-testid="screener-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>
            Model Screener
          </h1>
          <p className="text-xs mt-1" style={{ color: "var(--hoip-text-secondary)" }}>
            Regime-first, evidence-backed wheel setups — income strike re-scored against regime fit, P(non-touch) and historical edge.
          </p>
          <p className="text-[11px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
            Strikes, P(NT) and yield are evaluated at each ticker&apos;s nearest listed expiry to the selected DTE (see the <span style={{ color: "var(--hoip-text-secondary)" }}>Exp</span> column). Universe spans the wheel list plus every ticker the Scanner covers.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="evidence-as-of">
            <Clock className="w-3.5 h-3.5" /> Refreshed {timeAgo(data.evidence_as_of)}
            {data.evidence_oldest && data.evidence_oldest !== data.evidence_as_of && (
              <span data-testid="evidence-oldest" title="Not every row was rebuilt in the last refresh — the oldest surviving row dates from here. Stale rows are partitioned into the Stale tab."
                style={{ color: "#F59E0B" }}>· oldest row {timeAgo(data.evidence_oldest)}</span>
            )}
            {data.built_by_provider && (
              <span className="px-1.5 py-0.5 rounded-sm uppercase tracking-wide" data-testid="evidence-source"
                title="Refreshed = when analytics were recomputed. Underlying market data is delayed and cache-backed — not live pricing."
                style={{ color: "#F59E0B", background: "rgba(245,158,11,0.10)" }}>
                Delayed / cache-backed
              </span>
            )}
          </span>
          <ChainSourceBadge source={data.chain_source} />
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={building}
            data-testid="refresh-evidence-button" className="h-8 text-xs gap-1.5 border-[var(--hoip-border)]">
            <RefreshCw className={`w-3.5 h-3.5 ${building ? "animate-spin" : ""}`} />
            {building ? "Refreshing…" : "Refresh Evidence"}
          </Button>
        </div>
      </div>

      <ChainSourceBanner source={data.chain_source} />

      {/* Iter 107 — Paste-poisoned broker creds banner. Renders when
          /broker/active-source reports credentials that fail the latin-1
          encode test (typically Cyrillic look-alikes pasted in place of
          Latin letters). Without this banner, every Alpaca/Tradier call
          crashes with a cryptic codec error and the system silently falls
          back to yfinance for every quote/option — making the app feel
          extremely slow due to the 3-tier timeout cascade. */}
      {credErrors.length > 0 && (
        <div data-testid="cred-error-banner"
          className="mb-4 px-3 py-2 rounded-md text-xs flex items-start gap-2"
          style={{ background: "rgba(239,68,68,0.10)", color: "#EF4444",
                   border: "1px solid rgba(239,68,68,0.35)" }}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold mb-0.5">
              {credErrors.length === 1 ? credErrors[0].broker.toUpperCase() : "Broker"} credentials are corrupted —
              currently serving delayed market data for every quote and option chain.
            </div>
            <div className="opacity-90">
              {credErrors[0].error}
            </div>
            <a href="/profile?tab=brokers" data-testid="cred-error-fix-link"
              className="inline-block mt-1 underline font-semibold">
              Re-enter credentials in Profile → Brokers →
            </a>
          </div>
        </div>
      )}

      {/* Iter 104 — Trial-tier conversion chip. Renders only when the backend
          factory reports `trial_cache_window: true` (yfinance with the 15-min
          TTL window). Pure read of the existing /screener/model payload — no
          new fetch. Hidden for Pro users. */}
      {data?.factory?.trial_cache_window && (
        <a href="/billing" data-testid="trial-upgrade-chip"
          className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-md text-[11px] font-semibold no-underline"
          style={{ background: "rgba(245,158,11,0.10)", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.35)" }}>
          <Zap className="w-3.5 h-3.5" />
          Upgrade to a paid plan for advanced labs in development
          <ArrowRight className="w-3 h-3" />
        </a>
      )}

      {/* Controls: strategy + DTE horizon */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>Strategy</span>
          <div className="flex gap-1" data-testid="strategy-toggle">
            <Pill active={strategy === "csp"} onClick={() => setStrategy("csp")} testid="strategy-csp">Cash-Secured Put</Pill>
            <Pill active={strategy === "bps"} onClick={() => setStrategy("bps")} testid="strategy-bps">Bull Put Spread</Pill>
            <Pill active={strategy === "cc"} onClick={() => setStrategy("cc")} testid="strategy-cc">Covered Call</Pill>
            <Pill active={strategy === "pmcc"} onClick={() => setStrategy("pmcc")} testid="strategy-pmcc">PMCC / Fig Leaf</Pill>
          </div>
          {strategy === "cc" && (
            <span className="text-[10px] ml-1" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="cc-held-note">on held shares · Portfolio</span>
          )}
          {isCC && (
            <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none ml-2" style={{ color: "var(--hoip-text-secondary)" }} title="Hide held names that already have an open short call (from your Trade Log or linked broker). These are always excluded from the auto-campaign.">
              <input type="checkbox" checked={hideOpenCalls} onChange={e => setHideOpenCalls(e.target.checked)} className="accent-blue-600" data-testid="hide-open-calls" />
              Hide tickers with open calls
            </label>
          )}
          {isCC && (data?.kpis?.open_call_suppressed ?? 0) > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-sm" style={{ color: "#F59E0B", background: "rgba(245,158,11,0.12)" }} data-testid="open-calls-suppressed-indicator">
              {data.kpis.open_call_suppressed} ticker{data.kpis.open_call_suppressed === 1 ? "" : "s"} with open calls{hideOpenCalls ? " suppressed" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2" style={{ display: isPmcc ? "none" : undefined }}>
          <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>DTE</span>
          <div className="flex gap-1 flex-wrap" data-testid="dte-toggle">
            {horizons.map(h => (
              <Pill key={h.key} active={horizon === h.key} onClick={() => setHorizon(h.key)} testid={`dte-${h.key}`}>
                {h.label} <span className="opacity-60">{shortDate(h.expiry)}</span>
              </Pill>
            ))}
          </div>
        </div>
        {(strategy === "csp" || strategy === "bps") && (
          <div className="flex items-center gap-2" data-testid="strike-mode-toggle">
            <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }} title="Short-leg philosophy — the same toggle as Strategy Analysis. The long wing is always derived from the one shared risk engine.">Short method</span>
            <div className="flex gap-1">
              <Pill active={strikeMode === "delta"} onClick={() => setStrikeMode("delta")} testid="strike-mode-delta">Δ Regime-Scaled</Pill>
              <Pill active={strikeMode === "key_levels"} onClick={() => setStrikeMode("key_levels")} testid="strike-mode-key_levels">Key Levels</Pill>
              <Pill active={strikeMode === "pnt"} onClick={() => setStrikeMode("pnt")} testid="strike-mode-pnt">P(NT)</Pill>
            </div>
          </div>
        )}
        {/* Iter 106 — Sector shard dropdown. Both filters the displayed rows
            AND scopes the next Refresh (backend slices the universe BEFORE the
            burst loop, so a Utilities rebuild runs ~20 names instead of ~290). */}
        <div className="flex items-center gap-2" data-testid="sector-shard">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }} title="Limit the universe to one sector for faster rebuilds and a focused view.">Sector</span>
          <select value={sectorShard} onChange={e => setSectorShard(e.target.value)}
            data-testid="sector-shard-select"
            className="text-xs px-2 py-1 rounded border bg-transparent"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
            {sectorOptions.map(s => (
              <option key={s.name} value={s.name}>{s.name}{s.count ? ` (${s.count})` : ""}</option>
            ))}
          </select>
        </div>
        {/* Iter 106 — Hide Unaligned (Readiness gate) toggle. Drops rows whose
            execution verdict is "Parameters Outside Standard Band" (NO-GO) or
            whose DTE runs past the regime's P75 sojourn. Keeps the focus on
            high-probability setups. */}
        <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: "var(--hoip-text-secondary)" }}
          title="Hide trades whose parameters fall outside the historical confidence band, or that run past the regime's P75 visibility horizon.">
          <input type="checkbox" checked={hideUnaligned} onChange={e => setHideUnaligned(e.target.checked)}
            className="accent-blue-600" data-testid="hide-unaligned-toggle" />
          Hide unaligned trades
        </label>
      </div>

      {isPmcc ? <PmccView /> : (<>

      {/* KPI cards — honest partition: the four buckets always sum to the universe */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KpiCard testid="kpi-opportunities" label="Opportunities" value={loading ? "—" : (k.opportunity_count ?? 0)}
          sub={loading ? "loading…" : `${k.high_alignment ?? 0} high · ${k.moderate_alignment ?? 0} moderate`} icon={TrendingUp} accent="#3B82F6" />
        <KpiCard testid="kpi-conflicts" label="Conflicts / Excluded" value={loading ? "—" : (k.conflict_count ?? k.conflicts ?? 0)}
          sub={loading ? "loading…" : `${k.regime_mismatch ?? 0} regime mismatch`} icon={AlertTriangle} accent="#F59E0B" />
        <KpiCard testid="kpi-degraded" label="No Quote / Stale" value={loading ? "—" : ((k.degraded_count ?? 0) + (k.stale_count ?? 0))}
          sub={loading ? "loading…" : `${k.degraded_count ?? 0} no chain · ${k.stale_count ?? 0} stale`} icon={Gauge} accent="#EF4444" />
        <KpiCard testid="kpi-universe" label="Universe Scanned" value={loading ? "—" : (k.universe_count ?? 0)}
          sub={loading ? "loading…" : `${k.held_count ?? 0} held positions`} icon={Layers} />
      </div>

      {/* Tabs — strict partition, counts match renderable rows */}
      <Tabs value={tab} onValueChange={setTab} className="mb-3">
        <TabsList className="bg-transparent border-b border-[var(--hoip-border)] rounded-none p-0 h-auto w-full justify-start gap-1">
          {[
            { v: "opportunity", l: "Opportunities", c: data.opportunity?.length },
            { v: "conflict", l: "Conflicts / Excluded", c: data.conflict?.length },
            ...((data.degraded?.length || 0) > 0 ? [{ v: "degraded", l: "No Quote / Degraded", c: data.degraded?.length }] : []),
            ...((data.stale?.length || 0) > 0 ? [{ v: "stale", l: "Stale", c: data.stale?.length }] : []),
            { v: "portfolio", l: "Portfolio · Repair", c: data.portfolio?.length },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
              className="rounded-none border-b-2 border-transparent px-4 py-2 text-xs data-[state=active]:border-[var(--hoip-text-primary)] data-[state=active]:bg-transparent data-[state=active]:text-[var(--hoip-text-primary)]"
              style={{ color: "var(--hoip-text-secondary)" }}>
              {t.l} <span className="ml-1.5 opacity-60" style={MONO}>{t.c ?? 0}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Signal-threshold sliders — tighten/loosen each decision axis independently */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3 px-3 py-2 rounded-md border" data-testid="signal-sliders"
        style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
        <span className="text-[10px] uppercase tracking-wide font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Model thresholds</span>
        {[
          { key: "regime", label: "① Regime health", val: minRegimeHealth, set: setMinRegimeHealth, testid: "slider-regime-health",
            tip: "Regime health (0–100): how favourable AND fresh the current regime is — regime favourability × HMM confidence, discounted by freshness φ (an overextended regime, φ>1, is statistically 'due' to transition and scores lower). Raise to keep only names in healthy, non-stale regimes." },
          { key: "strike", label: "② Strike alignment", val: minStrikeAlign, set: setMinStrikeAlign, testid: "slider-strike-align",
            tip: "Strike alignment (0–100): how close the screener strike's delta is to the backtest-optimal delta for the current regime. 100 = perfectly aligned; a large gap means the strike is too rich/too far for this regime. Raise to keep only well-placed strikes." },
          { key: "sizing", label: "③ Sizing confidence", val: minSizing, set: setMinSizing, testid: "slider-sizing",
            tip: "Sizing confidence (0–100): how much size the evidence supports — blends the execution-gate recommended size, historical backtest quality, and P(not-touched). Raise to keep only high-conviction, well-sized trades." },
        ].map(s => (
          <label key={s.key} className="flex items-center gap-2 text-[11px] cursor-help" title={s.tip} style={{ color: "var(--hoip-text-secondary)" }}>
            <span className="whitespace-nowrap inline-flex items-center gap-1">{s.label}<Info className="w-2.5 h-2.5" style={{ color: "var(--hoip-text-tertiary)" }} /></span>
            <input type="range" min="0" max="100" step="5" value={s.val}
              onChange={e => s.set(Number(e.target.value))} data-testid={s.testid}
              className="accent-blue-600 cursor-pointer" style={{ width: 90 }} />
            <span className="font-mono-data w-9 text-right" style={{ color: s.val > 0 ? signalColor(s.val) : "var(--hoip-text-tertiary)" }} data-testid={`${s.testid}-val`}>
              ≥{s.val}
            </span>
          </label>
        ))}
        {(minRegimeHealth > 0 || minStrikeAlign > 0 || minSizing > 0) && (
          <button type="button" onClick={() => { setMinRegimeHealth(0); setMinStrikeAlign(0); setMinSizing(0); }}
            data-testid="reset-sliders-btn" className="text-[10px] underline" style={{ color: "var(--hoip-text-tertiary)" }}>
            Reset thresholds
          </button>
        )}
        <span className="text-[10px] ml-auto" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="signal-rows-count">
          {displayRows.length} shown{hiddenByFilters > 0 ? ` · ${hiddenByFilters} hidden by filters` : ""}
        </span>
      </div>

      {/* Inline filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--hoip-text-tertiary)" }} />
          <Input data-testid="screener-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Ticker…" className="h-8 w-40 pl-8 text-xs bg-[var(--hoip-card)] border-[var(--hoip-border)]" />
        </div>
        <select data-testid="filter-regime" value={regimeFilter} onChange={e => setRegimeFilter(e.target.value)}
          className="h-8 px-2 text-xs rounded-md border outline-none cursor-pointer"
          style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
          {REGIME_OPTIONS.map(o => <option key={o} value={o}>{o === "all" ? "All regimes" : o}</option>)}
        </select>
        <select data-testid="filter-classification" value={classFilter} onChange={e => setClassFilter(e.target.value)}
          className="h-8 px-2 text-xs rounded-md border outline-none cursor-pointer"
          style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
          {CLASS_OPTIONS.map(o => <option key={o} value={o}>{o === "all" ? "All alignments" : classDisplay(o)}</option>)}
        </select>
        <select data-testid="filter-verdict" value={verdictFilter} onChange={e => setVerdictFilter(e.target.value)}
          className="h-8 px-2 text-xs rounded-md border outline-none cursor-pointer"
          title="Readiness Check verdict — strategy-aware statistical gate summarising regime freshness, sojourn fit and credit viability (full breakdown on the Ticker page)."
          style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}>
          {["all", "GO", "CONDITIONAL", "NO-GO"].map(o => <option key={o} value={o}>{o === "all" ? "All verdicts" : verdictDisplay(o)}</option>)}
        </select>
        <Input data-testid="filter-min-pnt" value={minPnt} onChange={e => setMinPnt(e.target.value)}
          type="number" placeholder="Min P(NT) %" className="h-8 w-32 text-xs bg-[var(--hoip-card)] border-[var(--hoip-border)]" />
        <Input data-testid="filter-max-risk" value={maxRisk} onChange={e => setMaxRisk(e.target.value)}
          type="number" placeholder={isCC ? "Max shares $/ct" : isBps ? "Max risk $/ct" : "Max collateral $"} className="h-8 w-40 text-xs bg-[var(--hoip-card)] border-[var(--hoip-border)]"
          title="Risk-management rule of thumb: hide setups whose per-contract risk exceeds this. For BPS this is the defined max loss; for CSP it is the cash collateral." />
        <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: "var(--hoip-text-secondary)" }} title="Exclude small-cap / weak-fundamental names (always excluded from the auto-campaign regardless of this toggle).">
          <input type="checkbox" checked={hideWeak} onChange={e => setHideWeak(e.target.checked)} className="accent-blue-600" data-testid="filter-hide-weak" />
          Hide weak fundamentals
        </label>
      </div>

      {/* Quality / evidence filters — also constrain the auto-campaign */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>Restrict</span>
        <button type="button" onClick={resetFilters} data-testid="reset-filters-btn"
          className="text-[10px] underline underline-offset-2 transition-colors hover:text-[var(--hoip-text-primary)]"
          style={{ color: "var(--hoip-text-tertiary)" }} title="Clear all Restrict filters (keeps Strategy and DTE).">
          Reset filters
        </button>
        <Input data-testid="filter-min-yield" value={minYield} onChange={e => setMinYield(e.target.value)}
          type="number" placeholder="Min yield %" className="h-8 w-28 text-xs bg-[var(--hoip-card)] border-[var(--hoip-border)]"
          title="Hide setups whose premium yield (% of spot) is below this." />
        <Input data-testid="filter-min-ror" value={minRor} onChange={e => setMinRor(e.target.value)}
          type="number" placeholder="Min ROR %" className="h-8 w-28 text-xs bg-[var(--hoip-card)] border-[var(--hoip-border)]"
          title="Hide setups whose return-on-risk (BPS: credit ÷ max-loss; CSP: premium ÷ collateral) is below this." />
        <Input data-testid="filter-max-ror" value={maxRor} onChange={e => setMaxRor(e.target.value)}
          type="number" placeholder="Max ROR %" className="h-8 w-28 text-xs bg-[var(--hoip-card)] border-[var(--hoip-border)]"
          title="Hide setups whose ROR exceeds this — very high ROR usually means very low P(NT) / deep risk." />
        <Input data-testid="filter-max-dd" value={maxDD} onChange={e => setMaxDD(e.target.value)}
          type="number" placeholder="Max MaxDD $" className="h-8 w-32 text-xs bg-[var(--hoip-card)] border-[var(--hoip-border)]"
          title="Hide names whose backtest maximum drawdown ($, CSP hold-to-expiry over ~2y) exceeds this." />
        <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: "var(--hoip-text-secondary)" }} title="Drop setups with a negative backtest expectancy (average historical trade lost money on the CSP wheel).">
          <input type="checkbox" checked={excludeNegExp} onChange={e => setExcludeNegExp(e.target.checked)} className="accent-blue-600" data-testid="filter-exclude-neg-exp" />
          Exclude negative Exp$
        </label>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: "var(--hoip-text-secondary)" }} title="Hide setups whose selected expiry falls on or after the next earnings date — the open position would cross an earnings event (elevated IV and gap risk).">
          <input type="checkbox" checked={excludeEarnings} onChange={e => setExcludeEarnings(e.target.checked)} className="accent-blue-600" data-testid="filter-exclude-earnings" />
          Exclude earnings-spanning
        </label>
      </div>

      {/* Target-income campaign builder controls */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>$</span>
          <Input data-testid="filter-target-income" value={targetIncome} onChange={e => setTargetIncome(e.target.value)}
            type="number" placeholder="Target income / period" className="h-8 w-48 pl-5 text-xs bg-[var(--hoip-card)] border-[var(--hoip-border)]"
            title="The income you want to collect this period. Auto campaign sizes a diversified, quality-gated basket toward it; Manual lets you pick names and contracts yourself." />
        </div>
        <div className="flex gap-1" data-testid="basket-mode-toggle">
          <Pill active={!isManual} onClick={() => setBasketMode("auto")} testid="basket-mode-auto">Auto campaign</Pill>
          <Pill active={isManual} onClick={() => setBasketMode("manual")} testid="basket-mode-manual">Manual pick</Pill>
        </div>
        {!isManual && (
          <label className="flex items-center gap-1.5 text-xs" style={{ color: "var(--hoip-text-secondary)" }} title="Hard cap on contracts per name so no single ticker dominates the basket.">
            Max ct/name
            <Input type="number" min={1} value={maxPerName} onChange={e => setMaxPerName(Math.max(1, Number(e.target.value) || 1))}
              className="h-8 w-16 text-xs text-right bg-[var(--hoip-card)] border-[var(--hoip-border)]" data-testid="max-per-name-input" />
          </label>
        )}
        {isManual && (
          <button onClick={selectToTarget} disabled={!(Number(targetIncome) > 0)}
            className="h-8 px-3 text-xs rounded-md border transition-colors disabled:opacity-40 hover:bg-[var(--hoip-hover)]"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }} data-testid="select-to-target-btn">
            Select to target
          </button>
        )}
        {isManual && selected.size > 0 && (
          <button onClick={() => { setSelected(new Set()); setManualCts({}); }}
            className="h-8 px-3 text-xs rounded-md border transition-colors hover:bg-[var(--hoip-hover)]"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }} data-testid="clear-selection-btn">
            Clear
          </button>
        )}
      </div>

      {/* Portfolio Risk Control — NLV-based budgets that re-size the campaign */}
      {!isManual && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3" data-testid="risk-control-panel">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>Portfolio risk</span>
          <label className="flex items-center gap-1.5 text-xs" style={{ color: "var(--hoip-text-secondary)" }} title="Net liquidation value of your account. Risk budgets below are a percentage of this.">
            NLV
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>$</span>
              <Input type="number" min={0} value={nlv} onChange={e => setNlv(Math.max(0, Number(e.target.value) || 0))}
                className="h-8 w-28 pl-4 text-xs text-right bg-[var(--hoip-card)] border-[var(--hoip-border)]" data-testid="nlv-input" />
            </div>
          </label>
          <div className="flex gap-1" data-testid="risk-mode-toggle">
            {Object.entries(RISK_MODES).map(([key, m]) => (
              <Pill key={key} active={riskMode === key} onClick={() => setRiskMode(key)} testid={`risk-mode-${key}`}>{m.label}</Pill>
            ))}
          </div>
          <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            ≤{(riskCfg.totalPct * 100).toFixed(0)}% NLV {isBps ? "defined risk" : isCC ? "shares value" : "collateral"} · ≤{(riskCfg.perNamePct * 100).toFixed(0)}%/name · ≤{(riskCfg.sectorPct * 100).toFixed(0)}%/sector
          </span>
        </div>
      )}

      {/* Risk Budget Used bar */}
      {!isManual && campaign.totalBudget && Number(targetIncome) > 0 && autoBasket.length > 0 && (() => {
        const pct = Math.min(100, (campaign.riskUsed / campaign.totalBudget) * 100);
        const col = pct >= 95 ? "#EF4444" : pct >= 80 ? "#F59E0B" : "#10B981";
        return (
          <div className="mb-3 px-4 py-2.5 rounded-md border" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }} data-testid="risk-budget-bar">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span style={{ color: "var(--hoip-text-secondary)" }}>
                Risk Budget Used — <b style={{ color: "var(--hoip-text-primary)" }}>{pct.toFixed(0)}%</b> of {riskCfg.mode.label} {isBps ? "BPS" : isCC ? "CC" : "CSP"} budget
                <span style={{ color: "var(--hoip-text-tertiary)" }}> ({money(campaign.riskUsed)} / {money(campaign.totalBudget)} limit)</span>
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--hoip-hover)" }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: col }} data-testid="risk-budget-fill" />
            </div>
            {campaign.riskConstrained && (
              <div className="flex items-center gap-1.5 mt-2 text-[11px]" style={{ color: "#F59E0B" }} data-testid="risk-reduced-notice">
                <AlertTriangle className="w-3.5 h-3.5" /> Contracts reduced to fit risk budget — highest-risk names trimmed first to stay within {riskCfg.mode.label} caps.
              </div>
            )}
          </div>
        );
      })()}

      {/* Basket summary (target-income campaign) */}
      {basketSummary && (
        <div data-testid="basket-summary" className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2.5 mb-3 rounded-md border text-xs"
          style={{ borderColor: "#10B98166", background: "rgba(16,185,129,0.06)", color: "var(--hoip-text-secondary)" }}>
          <span style={{ color: "var(--hoip-text-primary)", fontWeight: 600 }}>
            Campaign to collect ${Number(targetIncome).toLocaleString()} / period
          </span>
          <span><b style={{ color: "var(--hoip-text-primary)" }}>{basketSummary.names}</b> names</span>
          <span><b style={{ color: "var(--hoip-text-primary)" }} data-testid="basket-contracts">{basketSummary.contracts}</b> contracts</span>
          <span>Premium <b style={{ color: "#10B981" }} data-testid="basket-premium">{money(basketSummary.premium)}</b>
            <span style={{ color: "var(--hoip-text-tertiary)" }}> ({basketSummary.pct.toFixed(0)}% of target)</span></span>
          <span>{isCC ? "Total shares value" : isBps ? "Total defined risk" : "Total capital"} <b style={{ color: isBps ? "#EF4444" : "var(--hoip-text-primary)" }} data-testid="basket-risk">{money(basketSummary.risk)}</b></span>
          <span>Blended win <b style={{ color: "var(--hoip-text-primary)" }}>{fmt(basketSummary.blendedWin, 0)}%</b></span>
          {basketSummary.pct < 100 && <span style={{ color: "#F59E0B" }}>· best achievable (quality-gated: ROR ≥ {ROR_FLOOR}%, ≤ {maxPerName} ct/name, no weak fundamentals)</span>}
          <Button size="sm" variant="outline" data-testid="log-basket-auto-btn" onClick={logBasket}
            disabled={logState === "saving"} className="h-7 text-[11px] gap-1.5 ml-auto"
            style={{ borderColor: logState === "done" ? "#10B981" : "var(--hoip-border)", color: logState === "done" ? "#10B981" : "var(--hoip-text-primary)" }}>
            <ClipboardList className="w-3.5 h-3.5" /> {logBtnLabel}
          </Button>
        </div>
      )}

      {/* Campaign earnings warning — spanning names elevate IV / gap risk */}
      {basketSummary && earningsSpanning.length > 0 && (
        <div data-testid="campaign-earnings-warning" className="flex items-center gap-1.5 px-4 py-2 mb-3 rounded-md border text-[11px]"
          style={{ borderColor: "#F59E0B66", background: "rgba(245,158,11,0.08)", color: "#F59E0B" }}>
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            ⚡ {earningsSpanning.length} ticker{earningsSpanning.length === 1 ? "" : "s"} in basket span earnings:{" "}
            <b>{earningsSpanning.join(", ")}</b>. Elevated IV and gap risk.
          </span>
        </div>
      )}

      {/* BS-estimated rows excluded from the campaign (real chain unavailable) */}
      {!isManual && (isBps || strategy === "csp") && bsExcludedCount > 0 && (
        <div data-testid="campaign-bs-excluded-note" className="flex items-center gap-1.5 px-4 py-2 mb-3 rounded-md border text-[11px]"
          style={{ borderColor: "#F59E0B44", background: "rgba(245,158,11,0.06)", color: "var(--hoip-text-secondary)" }}>
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#F59E0B" }} />
          <span>
            <b style={{ color: "#F59E0B" }}>{bsExcludedCount}</b> row{bsExcludedCount === 1 ? "" : "s"} excluded from the campaign — real option chain unavailable (Black-Scholes estimated premium).
          </span>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border overflow-auto max-h-[68vh]" style={{ borderColor: "var(--hoip-border)" }} data-testid="screener-table">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-10" style={{ background: "var(--hoip-card)" }}>
            <tr className="border-b text-xs" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
              {isManual && (
                <th className="px-2 py-2 text-center w-8">
                  <input type="checkbox" className="accent-blue-600" data-testid="select-all-checkbox"
                    checked={displayRows.length > 0 && displayRows.every(r => selected.has(r.ticker))}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelected(new Set(displayRows.map(r => r.ticker)));
                        setManualCts(m => { const n = { ...m }; displayRows.forEach(r => { if (!n[r.ticker]) n[r.ticker] = 1; }); return n; });
                      } else setSelected(new Set());
                    }} />
                </th>
              )}
              <SortHeader label="Ticker" field="ticker" align="left" title="Underlying symbol and last price" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Regime" field="regime" align="left" title="HMM hidden state + posterior confidence (model conviction)" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Flip" field="flip_risk" title="Probability the regime flips next bar (1 − self-transition)" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2 text-right font-medium" title={isCC ? "Covered call: regime-safe short call strike written against your held shares. 'cost' = your average cost basis." : isBps ? "Bull Put Spread: short leg (regime-safe strike) / long leg, and the width." : "Rich income short-put strike → regime-adjusted safe strike. 'gap' = credit given up per contract by trading the safer strike."}>
                {isCC ? "Call strike" : isBps ? "Short / Long" : "Income → Safe"}
              </th>
              <SortHeader label="Exp" field="dte" title="Target expiry — each ticker's nearest listed expiry to the selected DTE" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label={isCC ? "P(keep)" : "P(NT)"} field="_pnt" title={isCC ? "Probability the stock stays BELOW the call strike (shares are NOT called away)" : "Regime-adjusted probability the short strike is NOT touched by expiry"} sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Δ" field="_delta" title={isCC ? "Call delta (≈ probability shares get called away)" : "Short-leg option delta (≈ assignment probability)"} sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label={isBps ? "Credit $" : "Prem $"} field="_prem" title={isCC ? "Call premium collected per contract ($) — income on shares you already own" : isBps ? "Net credit received per spread contract ($)" : "Premium received per contract ($)"} sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Yield %" field="_yield" title={isCC ? "Static yield = call premium ÷ your cost basis (return if NOT called)" : isBps ? "Income-strike premium as a % of spot (underlying premium richness)" : "Safe-strike premium as a % of spot"} sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label={isCC ? "If-called %" : "ROR %"} field="_ror" title={isCC ? "Total return if called away = (premium + (call strike − cost basis)) ÷ cost basis" : isBps ? "Return on risk = net credit ÷ max loss (defined-risk leverage)" : "Return on collateral = premium ÷ cash secured (yield on capital at risk)"} sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label={isCC ? "Shares $" : "Risk $"} field="_risk" title={isCC ? "Market value of the shares at risk = spot × 100 (a covered call cushions but doesn't define downside)" : isBps ? "Defined max loss per contract = (width − credit) × 100" : "Cash collateral per contract = strike × 100"} sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2 text-right font-medium" title={isCC ? "Covered-call contracts to write — capped at your held shares ÷ 100. Enter a Target income to auto-size." : "Position sizing for your Target income this period: contracts to sell, and the total capital (CSP) or defined risk (BPS) it requires. Enter a Target income to populate."}>Sizing</th>
              <SortHeader label="Win%" field="bt_win_rate" title="Backtest win rate over 2y of non-overlapping trades at this DTE" sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="N" field="bt_sample_size" title="Backtest sample size — number of historical trades (≈ 2y of bars ÷ DTE)." sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Trade Readiness" field="composite_score" align="left" title="Sequential decision panel: ① Regime health (favourability × confidence × freshness φ) → ② Strike alignment (current Δ vs regime-optimal Δ) → ③ Sizing confidence (evidence + execution gate). Every axis is a gauge, not a hard block — use the Model-threshold sliders to filter each axis." sortField={sortField} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2 text-right font-medium" title="Open the ticker workbench (broker-accurate strikes/greeks for Pro accounts)">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={16 + (isManual ? 1 : 0)} className="px-3 py-10 text-center text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>Loading evidence…</td></tr>
            ) : building && rows.length === 0 ? (
              <tr><td colSpan={16 + (isManual ? 1 : 0)} className="px-3 py-10 text-center text-xs" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="screener-rebuild-skeleton">
                <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                {(strategy === "csp" || strategy === "bps")
                  ? <>Rebuilding evidence for <span style={{ color: "var(--hoip-text-secondary)" }}>{STRIKE_MODE_LABELS[strikeMode] || strikeMode}</span> mode… this runs once per mode &amp; horizon, then loads instantly.</>
                  : <>Building evidence for the wheel universe… this runs once per horizon, then loads instantly.</>}
              </td></tr>
            ) : displayRows.length === 0 ? (
              <tr><td colSpan={16 + (isManual ? 1 : 0)} className="px-3 py-10 text-center text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>No setups match the current filters.</td></tr>
            ) : displayRows.map(r => (
              <tr key={r.ticker} data-testid={`screener-row-${r.ticker}`}
                className="border-b transition-colors hover:bg-[var(--hoip-hover)]"
                style={{ borderColor: "var(--hoip-border)", background: isManual && selected.has(r.ticker) ? "rgba(59,130,246,0.06)" : qualityTint(r.fundamental_label) }}>
                {isManual && (
                  <td className="px-2 py-2 text-center">
                    <input type="checkbox" className="accent-blue-600" checked={selected.has(r.ticker)}
                      onChange={() => toggleSelect(r.ticker)} data-testid={`select-${r.ticker}`} />
                  </td>
                )}
                <td className="px-3 py-2 text-left">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium" style={{ color: "var(--hoip-text-primary)" }}>{r.ticker}</span>
                    {r.held && <span className="text-[9px] px-1 rounded-sm" style={{ color: "#10B981", background: "rgba(16,185,129,0.12)" }}>HELD</span>}
                    <ProviderChip source={r.chain_source || r.data_source} degraded={r.degraded} />
                    {r.low_quality && (
                      <span className="text-[9px] px-1 rounded-sm" style={{ color: "#F59E0B", background: "rgba(245,158,11,0.12)" }}
                        title={`Fundamental quality: ${r.fundamental_label}${r.market_cap ? ` · mkt cap $${(r.market_cap / 1e9).toFixed(1)}B` : ""}. Excluded from the auto-campaign.`}>
                        {r.fundamental_label}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] inline-flex items-center gap-1" style={{ ...MONO, color: "var(--hoip-text-tertiary)" }}>
                    ${fmt(r.price, 2)}
                    {(() => {
                      // Per-ROW chronology (audit Bug #2 fix): this row's own
                      // computed_at — never the cache-wide max. Failed rows say so.
                      const rs = r.stale
                        ? { label: `stale · refresh failed`, color: "#EF4444", warn: true,
                            tip: r.last_error ? `Last refresh failed: ${r.last_error}` : "This row could not be recomputed in the last refresh." }
                        : staleInfo(r.computed_at || data.evidence_as_of);
                      if (!rs) return null;
                      return (
                        <span data-testid={`price-stale-${r.ticker}`}
                          style={{ color: rs.color }}>· {rs.label}{rs.warn ? " ⚠" : ""}</span>
                      );
                    })()}
                    {(r.stale || r.degraded) && (
                      <RescreenButton ticker={r.ticker} horizon={horizon} strikeMode={strikeMode} onPatched={patchRow} />
                    )}
                  </span>
                </td>
                <td className="px-3 py-2 text-left"><RegimeCell regime={r.regime} posterior={r.posterior_conf} /><RegimeAlignmentDot entry={regimeEdges[r.ticker]} currentRegime={r.regime} />
                  <RegimeSojournBar regime={r.regime} elapsedDays={r.regime_age_bars} medianSojournDays={r.median_sojourn_days}
                    dte={r.snapped_dte} transitionProbs={r.transition_probs} /></td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: (r.flip_risk ?? 0) >= 40 ? "#F59E0B" : "var(--hoip-text-secondary)" }}>{fmt(r.flip_risk, 0)}%</td>
                <td className="px-3 py-2 text-right">{isCC ? <CcStrikes row={r} /> : isBps ? <BpsStrikes row={r} /> : <CspStrikes row={r} />}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex flex-col items-end leading-tight" style={MONO}>
                    <span style={{ color: "var(--hoip-text-secondary)" }}>{r.dte != null ? `${r.dte}D` : "—"}</span>
                    {r.expiry && <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{shortDate(r.expiry)}</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-primary)" }}>{r._pnt != null ? `${fmt(r._pnt, 0)}%` : "—"}</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-secondary)" }}>{r._delta != null ? fmt(r._delta, 2) : "—"}</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-primary)" }}>{money(r._prem)}</td>
                <td className="px-3 py-2 text-right" data-testid={`yield-cell-${r.ticker}`}>
                  {r._yield == null ? (
                    <span style={{ ...MONO, color: "var(--hoip-text-secondary)" }}>—</span>
                  ) : (() => {
                    const tgt = regimeYieldTarget(r.regime);
                    const below = !isCC && r._yield < tgt;
                    return (
                      <span style={{ ...MONO, color: below ? "#F59E0B" : "var(--hoip-text-secondary)" }}
                        data-testid={below ? `yield-warning-${r.ticker}` : undefined}
                        title={below ? `Yield ${fmt(r._yield, 2)}% below ${r.regime} minimum (${tgt}%)` : undefined}>
                        {fmt(r._yield, 2)}%{below ? " ⚠" : ""}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: isCC ? ((r._ror ?? 0) < 0 ? "#EF4444" : "#10B981") : isBps ? "#10B981" : "var(--hoip-text-primary)" }}>{r._ror != null ? `${fmt(r._ror, isBps ? 0 : 2)}%` : "—"}</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-secondary)" }}>{money(r._risk)}</td>
                <td className="px-3 py-2 text-right" data-testid={`sizing-${r.ticker}`}>
                  {isManual ? (
                    <div className="flex flex-col items-end leading-tight" style={MONO}>
                      <input type="number" min={1} max={r._max_ct ?? undefined} value={manualCts[r.ticker] ?? 1}
                        onChange={e => setCts(r.ticker, e.target.value, r._max_ct)}
                        onFocus={() => { if (!selected.has(r.ticker)) toggleSelect(r.ticker); }}
                        disabled={!selected.has(r.ticker)}
                        className="w-14 h-6 text-right text-xs rounded-sm border bg-transparent px-1 disabled:opacity-40"
                        style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
                        data-testid={`cts-input-${r.ticker}`} />
                      {selected.has(r.ticker) && (
                        <span className="text-[10px]" style={{ color: "#10B981" }}>{money((manualCts[r.ticker] ?? 1) * (r._prem || 0))} prem</span>
                      )}
                      {selected.has(r.ticker) && r._risk != null && (
                        <span className="text-[10px]" style={{ color: isBps ? "#EF4444" : "var(--hoip-text-tertiary)" }}>{money((manualCts[r.ticker] ?? 1) * r._risk)} {isCC ? "shares" : isBps ? "risk" : "cap"}</span>
                      )}
                      {isCC && r._max_ct != null && <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>max {r._max_ct} ({r.held_shares} sh)</span>}
                    </div>
                  ) : r._contracts ? (
                    <div className="flex flex-col items-end leading-tight" style={MONO} data-testid={`contracts-cell-${r.ticker}`}>
                      <span className="font-medium" style={{ color: "var(--hoip-text-primary)" }} data-testid={`contracts-${r.ticker}`}>{r._contracts}×</span>
                      <span className="text-[10px]" style={{ color: "#10B981" }}>{money(r._premCollected)} prem</span>
                      {r._capRisk != null && <span className="text-[10px]" style={{ color: isBps ? "#EF4444" : "var(--hoip-text-tertiary)" }}>{money(r._capRisk)} {isCC ? "shares" : isBps ? "risk" : "cap"}</span>}
                      {r._binding && <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid={`binding-${r.ticker}`} title="The constraint that capped this position's size">· {r._binding}</span>}
                    </div>
                  ) : (perNameBudgetUI > 0 && r._risk > 0) ? (
                    <div className="flex flex-col items-end leading-tight" style={MONO} data-testid={`maxct-cell-${r.ticker}`}>
                      <span className="font-medium" style={{ color: "var(--hoip-text-secondary)" }} data-testid={`maxct-${r.ticker}`}>{Math.floor(perNameBudgetUI / r._risk)}× max</span>
                      <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>per-name cap</span>
                    </div>
                  ) : <span style={{ color: "var(--hoip-text-tertiary)" }}>—</span>}
                </td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: "var(--hoip-text-secondary)" }}>{fmt(isCC ? r.cc_bt_win_rate : r.bt_win_rate, 0)}%</td>
                <td className="px-3 py-2 text-right" style={{ ...MONO, color: ((isCC ? r.cc_bt_sample_size : r.bt_sample_size) ?? 0) < 15 ? "var(--hoip-text-tertiary)" : "var(--hoip-text-secondary)" }}>{(isCC ? r.cc_bt_sample_size : r.bt_sample_size) ?? 0}</td>
                <td className="px-3 py-2 text-left">
                  <div className="flex flex-col gap-1 items-start">
                    {isCC && r.open_short_call && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-sm" style={{ color: "#F59E0B", background: "rgba(245,158,11,0.12)" }} data-testid={`open-call-badge-${r.ticker}`} title="You already have an open short call on this name — writing another would stack/over-write. Excluded from the auto-campaign.">
                        Call already written
                      </span>
                    )}
                    {isProOrAdmin && <TradeReadinessCell r={r} strategy={strategy} />}
                    {r._price_estimated && (() => {
                      const broker = r.chain_source === "alpaca" || r.chain_source === "tradier";
                      const label = broker ? "⚠ Chain unavailable — check expiry/strike" : "⚠ BS-estimated — real chain unavailable";
                      const tip = broker
                        ? "The broker returned no quote for this strike/expiry — it cannot be priced from the real chain and is excluded from the auto-campaign. Check the strike/expiry availability."
                        : "Real option chain unavailable for this strike/expiry — premium is Black-Scholes estimated. Excluded from the auto-campaign.";
                      return (
                        <span data-testid={`bs-estimated-${r.ticker}`} className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm whitespace-nowrap"
                          style={{ color: "#F59E0B", background: "rgba(245,158,11,0.12)", border: "1px solid #F59E0B55" }} title={tip}>
                          {label}
                        </span>
                      );
                    })()}
                    {(() => {
                      const ec = classifyEarnings(earnings[r.ticker], r.expiry);
                      const showIvr = r.iv_rank !== null && r.iv_rank !== undefined;
                      if (!ec && !showIvr) return null;
                      return (
                        <div className="flex items-center gap-1 flex-wrap mt-0.5">
                          <IvrBadge ivr={r.iv_rank} ticker={r.ticker} method={r.iv_rank_method} />
                          <EarningsChip cls={ec} ticker={r.ticker} />
                        </div>
                      );
                    })()}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" data-testid={`inspect-${r.ticker}`}
                    onClick={() => {
                      const sig = r._sig || computeTradeSignals(r, strategy);
                      const sphi = sig?.phi != null ? `&sphi=${sig.phi}` : "";
                      const exp = r.expiry ? `&expiry=${r.expiry}` : "";
                      // ── Phase 2: cross-module pass-through ──
                      // Forward the resolved row snapshot via router state so the
                      // Workbench can seed its initial view from the SAME analytics
                      // payload the Screener rendered (regime label, DTE/expiry,
                      // short delta, P(NT), φ) — no net-new chain fetch on mount.
                      // The Workbench falls back to fetching when state is missing
                      // (refresh-safe).
                      navigate(`/ticker/${r.ticker}?mode=${strikeMode}${sphi}${exp}`, {
                        state: {
                          screenerSnapshot: r,
                          screenerHorizon: horizon,
                          screenerStrikeMode: strikeMode,
                          // Iter 103 add-on — forward the screener's build telemetry
                          // (provider that built the cache + when the cache was
                          // sealed) so the Workbench header can render a "Last
                          // rebuild …" context chip WITHOUT issuing a new fetch.
                          screenerEvidenceAsOf: data?.evidence_as_of || null,
                          screenerBuiltByProvider: data?.built_by_provider || data?.data_source || null,
                        },
                      });
                    }}
                    className="h-7 text-[11px] gap-1 text-[var(--hoip-text-secondary)] hover:text-[var(--hoip-text-primary)]">
                    Inspect <ArrowRight className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
            {isManual && manualSummary.names > 0 && (
              <tr aria-hidden="true"><td colSpan={16 + (isManual ? 1 : 0)} style={{ height: 56, border: "none" }} /></tr>
            )}
          </tbody>
        </table>
        {/* Manual selection footer */}
        {isManual && manualSummary.names > 0 && (
          <div className="px-4 py-2.5 border-t flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sticky bottom-0"
            style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }} data-testid="manual-footer">
            <span className="font-semibold" style={{ color: "var(--hoip-text-primary)" }}>
              <b data-testid="manual-names">{manualSummary.names}</b> selected · <b data-testid="manual-contracts">{manualSummary.contracts}</b> contracts
            </span>
            <span>Premium <b style={{ color: "#10B981" }} data-testid="manual-premium">{money(manualSummary.premium)}</b>
              {Number(targetIncome) > 0 && <span style={{ color: "var(--hoip-text-tertiary)" }}> ({manualSummary.pct.toFixed(0)}% of target)</span>}</span>
            <span>{isCC ? "Shares value" : isBps ? "Defined risk" : "Capital"} <b style={{ color: isBps ? "#EF4444" : "var(--hoip-text-primary)" }} data-testid="manual-risk">{money(manualSummary.risk)}</b></span>
            <span>Prem/Risk <b style={{ color: "var(--hoip-text-primary)" }}>{manualSummary.premRiskPct.toFixed(1)}%</b></span>
            <span>Blended win <b style={{ color: "var(--hoip-text-primary)" }}>{fmt(manualSummary.blendedWin, 0)}%</b></span>
            {Number(targetIncome) > 0 && (
              <div className="flex-1 min-w-[120px] max-w-[260px] h-1.5 rounded-full overflow-hidden ml-auto" style={{ background: "var(--hoip-hover)" }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${manualSummary.pct}%`, background: manualSummary.pct >= 100 ? "#10B981" : manualSummary.pct >= 70 ? "#F59E0B" : "#EF4444" }} />
              </div>
            )}
            <Button size="sm" variant="outline" data-testid="log-basket-manual-btn" onClick={logBasket}
              disabled={logState === "saving"} className="h-7 text-[11px] gap-1.5 ml-auto"
              style={{ borderColor: logState === "done" ? "#10B981" : "var(--hoip-border)", color: logState === "done" ? "#10B981" : "var(--hoip-text-primary)" }}>
              <ClipboardList className="w-3.5 h-3.5" /> {logBtnLabel}
            </Button>
          </div>
        )}
      </div>

      {/* What this table synthesizes — Model Alignment legend */}
      <div className="mt-4 rounded-md border p-4 text-[11px] leading-relaxed" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)", color: "var(--hoip-text-secondary)" }} data-testid="screener-legend">
        <div className="text-xs font-semibold mb-1.5" style={{ color: "var(--hoip-text-primary)", fontFamily: "Chivo, sans-serif" }}>What this table synthesizes</div>
        <p className="mb-2">
          Each row blends four evidence layers into one neutral read, weighted <b>regime fit (40%) → backtest quality (30%) → option suitability / P(NT) (20%) → premium (10%)</b>.
          The <b>Model Alignment</b> badge is that combined verdict — it is a model output, <b>not</b> investment advice.
          Win% and N come from a ~2-year backtest of the <b>cash-secured-put wheel held to expiry</b> (the same hold-to-expiry proxy for every name), so they describe the underlying&apos;s historical behaviour at this DTE, not the exact BPS strike shown.
        </p>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-2">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm border whitespace-nowrap" style={{ color: CLASS_STYLES["High Model Alignment"].color, borderColor: CLASS_STYLES["High Model Alignment"].border }}>High Model Alignment</span>
            <span>Regime fit, historical edge and option suitability are <b>all</b> strong — the cleanest setups under the current regime.</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="mt-0.5 inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm border whitespace-nowrap" style={{ color: CLASS_STYLES["Moderate Alignment"].color, borderColor: CLASS_STYLES["Moderate Alignment"].border }}>Moderate Alignment</span>
            <span>Generally supportive evidence, but not every factor is strong — workable with selection.</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="mt-0.5 inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm border whitespace-nowrap" style={{ color: CLASS_STYLES["Mixed Signal"].color, borderColor: CLASS_STYLES["Mixed Signal"].border }}>Mixed Indicators</span>
            <span>Factors <b>conflict</b> — e.g. rich premium but a weak historical edge, or a strong strike in a so-so regime. The inline note states the specific conflict.</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="mt-0.5 inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm border whitespace-nowrap" style={{ color: CLASS_STYLES["Regime Mismatch"].color, borderColor: CLASS_STYLES["Regime Mismatch"].border }}>Regime Mismatch</span>
            <span>The regime <b>contradicts</b> the trade — bearish regime or high downside-flip risk for a put-selling setup. Treated as a conflict, not an opportunity.</span>
          </div>
        </div>
        <p className="mt-3" style={{ color: "var(--hoip-text-tertiary)" }}>
          Expectancy ($) and max-drawdown are no longer shown as columns to reduce noise — you can still gate on them with the <b>Exclude negative Exp$</b> and <b>Max MaxDD $</b> filters above.
          BPS legs are anchored on the regime-safe short strike. <b>Covered Call</b> mode is held-only (Portfolio tab): it writes a regime-safe call against shares you own, sized to your held shares — Yield % is premium ÷ your cost basis (return if not called) and If-called % is the total return if shares are called away. Broker-accurate strikes/greeks are shown on Inspect for Pro accounts. For educational purposes only — not investment advice.
        </p>
        <p className="mt-2" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="screener-methodology-disclosure">
          Model outputs depend on the assumptions and data inputs described above. Historical and simulated data may
          be incomplete or subject to error. Simulated results are not guarantees of future performance.
        </p>
      </div>
      </>)}
    </div>
  );
}
